/**
 * Shared, UI-agnostic logic for student Session Progress Reports.
 *
 * Extracted from ProgressReportWorkspace so the same metric/AI/report-document
 * pipeline can be reused by the student-specific Sessions tab without
 * duplicating behavior. Pure helpers + a `createProgressReport` orchestrator
 * (AI call → save) live here; all UI stays in the consuming components.
 */
import { Timestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { getAuth } from "firebase/auth";

import { functions as firebaseFunctions } from "../../firebaseConfig";
import { toDateMaybe, calculateRoundedUsedHours } from "../tutor/session_logs/sessionLogApi";
import { groupSubjectHours } from "../../utils/subjectNormalization";
import { saveProgressReport, buildReportId } from "./progressReportApi";

// ─── date / formatting helpers ──────────────────────────────────────────────

export function toDateKey(value) {
  const d = toDateMaybe(value);
  if (!d) return "";
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, "0"),
    String(d.getDate()).padStart(2, "0"),
  ].join("-");
}

export function formatDateDisplay(value) {
  if (!value) return "—";
  const d = toDateMaybe(value);
  if (!d) {
    const parts = String(value).split("-");
    if (parts.length === 3) {
      const dt = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
      if (!Number.isNaN(dt.getTime())) {
        return dt.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
      }
    }
    return String(value);
  }
  return d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
}

export function formatDateRange(start, end) {
  return `${formatDateDisplay(start)} – ${formatDateDisplay(end)}`;
}

export function toMillis(value) {
  const d = toDateMaybe(value);
  return d ? d.getTime() : 0;
}

export function formatTime(value) {
  const d = toDateMaybe(value);
  if (!d) return "—";
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

export function sortByDateDesc(logs) {
  return [...logs].sort((a, b) => {
    const aMs = toDateMaybe(a?.sessionStart)?.getTime() || 0;
    const bMs = toDateMaybe(b?.sessionStart)?.getTime() || 0;
    return bMs - aMs;
  });
}

export function getUsedHours(log) {
  const h = Number(log?.usedHours);
  if (Number.isFinite(h) && h >= 0) return h;
  return calculateRoundedUsedHours(log?.sessionStart, log?.sessionEnd);
}

export function hwBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "completed") return "prw-badge--completed";
  if (s.includes("partial")) return "prw-badge--partial";
  if (s === "not done") return "prw-badge--notdone";
  return "prw-badge--neutral";
}

export function buildReportTitle(report) {
  const custom = String(report?.customName || "").trim();
  if (custom) return custom;
  const dateRange = formatDateRange(report.startDate, report.endDate);
  const base = `Progress Report — ${dateRange}`;
  if (report?.studentName) return `${report.studentName} — ${base}`;
  return base;
}

// ─── metric builder ──────────────────────────────────────────────────────────

export function buildMetrics(filteredLogs) {
  const total = filteredLogs.length;
  const totalHours = filteredLogs.reduce((s, l) => s + getUsedHours(l), 0);

  const hwStatuses = filteredLogs.map((l) =>
    String(l?.homeworkStatus || "").toLowerCase()
  );
  const hwCompleted = hwStatuses.filter((s) => s === "completed").length;
  const hwPartial = hwStatuses.filter((s) => s.includes("partial")).length;
  const hwNotDone = hwStatuses.filter((s) => s === "not done").length;
  const hwNotAssigned = hwStatuses.filter((s) => s === "not assigned").length;
  const hwAssigned = hwCompleted + hwPartial + hwNotDone;
  const hwCompletionRate =
    hwAssigned > 0 ? Math.round((hwCompleted / hwAssigned) * 100) : null;

  const fields = ["effort", "motivation", "behavior", "focus", "confidence"];
  const avgRatings = {};
  fields.forEach((f) => {
    const vals = filteredLogs
      .map((l) => {
        const v = l?.ratings?.[f];
        if (v == null || v === "") return null;
        const n = Number(v);
        return Number.isFinite(n) && n > 0 ? n : null;
      })
      .filter((v) => v !== null);
    avgRatings[f] = vals.length
      ? Math.round((vals.reduce((s, v) => s + v, 0) / vals.length) * 10) / 10
      : null;
  });

  const riskFlags = filteredLogs.map((l) =>
    String(l?.studentFlag || "").toLowerCase()
  );
  const atRiskCount = riskFlags.filter((f) => f === "at risk").length;
  const needsAttnCount = riskFlags.filter((f) => f === "needs attention").length;

  let riskLevel = "On Track";
  if (
    atRiskCount > 0 ||
    (hwCompletionRate !== null && hwCompletionRate < 50) ||
    (avgRatings.focus !== null && avgRatings.focus < 3)
  ) {
    riskLevel = "At Risk";
  } else if (
    needsAttnCount > 0 ||
    (hwCompletionRate !== null && hwCompletionRate < 70) ||
    (avgRatings.motivation !== null && avgRatings.motivation < 3.5)
  ) {
    riskLevel = "Needs Attention";
  }

  const rawSubjectHours = {};
  filteredLogs.forEach((l) => {
    const sub = String(l?.subject || "General").trim() || "General";
    rawSubjectHours[sub] = (rawSubjectHours[sub] || 0) + getUsedHours(l);
  });

  return {
    totalSessions: total,
    totalHours: Math.round(totalHours * 100) / 100,
    homeworkCompletionRate: hwCompletionRate,
    homeworkCompleted: hwCompleted,
    homeworkPartial: hwPartial,
    homeworkNotDone: hwNotDone,
    homeworkNotAssigned: hwNotAssigned,
    avgEffort: avgRatings.effort,
    avgMotivation: avgRatings.motivation,
    avgBehavior: avgRatings.behavior,
    avgFocus: avgRatings.focus,
    avgConfidence: avgRatings.confidence,
    riskLevel,
    rawSubjectHours,
    normalizedHours: groupSubjectHours(rawSubjectHours),
    subjects: [...new Set(filteredLogs.map((l) => String(l?.subject || "").trim()).filter(Boolean))],
    tutors: [...new Set(filteredLogs.map((l) => String(l?.tutorName || "").trim()).filter(Boolean))],
    topicsCovered: [
      ...new Set(filteredLogs.map((l) => String(l?.topicCovered || "").trim()).filter(Boolean)),
    ].slice(0, 30),
  };
}

// ─── AI payload builder ────────────────────────────────────────────────────────

export function buildAiPayload(filteredLogs, studentName, startDate, endDate, metrics) {
  const sorted = sortByDateDesc(filteredLogs);

  const recentLessonNotes = sorted
    .slice(0, 25)
    .map((l) => [String(l?.lessonActivity || "").trim(), String(l?.topicCovered || "").trim()].filter(Boolean).join(". "))
    .filter(Boolean)
    .join(" | ")
    .slice(0, 2000);

  const recentNextFocus = sorted
    .slice(0, 15)
    .map((l) => [String(l?.nextFocus || "").trim(), String(l?.ai?.nextSessionPlan || "").trim()].filter(Boolean).join(". "))
    .filter(Boolean)
    .join(" | ")
    .slice(0, 1200);

  const recentHomeworkComments = sorted
    .slice(0, 15)
    .map((l) => String(l?.homeworkComments || "").trim())
    .filter(Boolean)
    .join(" | ")
    .slice(0, 800);

  const recentLearningInsights = sorted
    .slice(0, 15)
    .map((l) => String(l?.learningInsight || "").trim())
    .filter(Boolean)
    .join(" | ")
    .slice(0, 1000);

  return {
    studentName,
    startDate,
    endDate,
    totalSessions: metrics.totalSessions,
    totalHours: metrics.totalHours,
    subjects: metrics.subjects,
    tutors: metrics.tutors,
    topicsCovered: metrics.topicsCovered.slice(0, 20),
    normalizedHours: metrics.normalizedHours,
    metrics: {
      homeworkCompletionRate: metrics.homeworkCompletionRate,
      homeworkCompleted: metrics.homeworkCompleted,
      homeworkPartial: metrics.homeworkPartial,
      homeworkNotDone: metrics.homeworkNotDone,
      avgEffort: metrics.avgEffort,
      avgMotivation: metrics.avgMotivation,
      avgBehavior: metrics.avgBehavior,
      avgFocus: metrics.avgFocus,
      riskLevel: metrics.riskLevel,
    },
    recentLessonNotes,
    recentNextFocus,
    recentHomeworkComments,
    recentLearningInsights,
  };
}

// ─── Local AI fallback ───────────────────────────────────────────────────────

export function buildLocalAiFallback(metrics, studentName) {
  const name = studentName || "The student";
  const hwRate = metrics.homeworkCompletionRate !== null ? `${metrics.homeworkCompletionRate}%` : "N/A";
  const effort = metrics.avgEffort ? `${metrics.avgEffort}/5` : "not recorded";
  const focus = metrics.avgFocus ? `${metrics.avgFocus}/5` : "not recorded";
  const subjects = metrics.subjects.slice(0, 3).join(", ") || "assigned subjects";
  const topTopics = metrics.topicsCovered.slice(0, 4).join(", ") || "covered topics";

  const strengths = [];
  if (metrics.avgEffort !== null && metrics.avgEffort >= 4)
    strengths.push("Demonstrates strong and consistent effort during each session.");
  if (metrics.avgFocus !== null && metrics.avgFocus >= 4)
    strengths.push("Maintains solid focus throughout tutoring sessions.");
  if (metrics.homeworkCompletionRate !== null && metrics.homeworkCompletionRate >= 70)
    strengths.push("Homework completion has been consistent and reliable.");
  if (!strengths.length)
    strengths.push("Shows engagement and willingness to participate in tutoring sessions.");

  const improvements = [];
  if (metrics.homeworkCompletionRate !== null && metrics.homeworkCompletionRate < 70)
    improvements.push("Improving homework completion consistency will reinforce concepts between sessions.");
  if (metrics.avgMotivation !== null && metrics.avgMotivation < 3.5)
    improvements.push("Building motivation through achievable short-term goals will strengthen progress.");
  if (!improvements.length)
    improvements.push("Continuing to strengthen independent practice and concept retention between sessions.");

  return {
    provider: "local_fallback",
    riskLevel: metrics.riskLevel,
    overallProgress:
      `${name} completed ${metrics.totalSessions} tutoring sessions, ` +
      `accumulating ${metrics.totalHours.toFixed(1)} total hours across ${subjects}. ` +
      `Overall progress is tracking at ${metrics.riskLevel.toLowerCase()}.`,
    academicProgress:
      `During this period, ${name} worked through topics including ${topTopics}. ` +
      `Session activities reflect steady academic engagement and gradual skill development.`,
    classPerformance:
      `${name}'s in-session performance reflects a ${metrics.riskLevel.toLowerCase()} trajectory. ` +
      `${metrics.avgEffort !== null ? `Average effort is rated at ${effort} and focus at ${focus}.` : "Engagement has been noted by the tutoring team."}`,
    homeworkAnalysis:
      `Homework completion stands at ${hwRate} for this reporting period. ` +
      `Consistent assignment follow-through is an important factor in reinforcing session concepts.`,
    learningHabitsNarrative:
      `${name} demonstrates ${metrics.avgEffort !== null && metrics.avgEffort >= 4 ? "strong" : "developing"} learning habits. ` +
      `Regular participation and follow-through between sessions are key focus areas going forward.`,
    keyStrengths: strengths,
    areasForImprovement: improvements,
    instructorComments:
      `Based on this reporting period, ${name} has shown dedication to their academic goals. ` +
      `The tutoring team recommends continued focus on the identified subject areas. ` +
      `Regular attendance and homework completion remain important contributors to academic growth.`,
    goalsAndActionPlan: [
      "Continue consistent session attendance and active participation.",
      "Complete assigned practice work between sessions to reinforce covered concepts.",
      "Review and revisit topics where understanding remains incomplete.",
      "Set one measurable academic goal for the next reporting period.",
    ],
  };
}

// ─── report document builder ───────────────────────────────────────────────────

export function buildReportDocument({
  reportId,
  studentId,
  studentName,
  startDate,
  endDate,
  generatedAtMs,
  generatedByUid,
  filteredLogs,
  lastSessionDateKey,
  metrics,
  aiResult,
}) {
  return {
    reportId,
    studentId,
    studentName,
    startDate,
    endDate,
    generatedAt: Timestamp.fromMillis(generatedAtMs),
    generatedByUid: generatedByUid || "",
    status: "complete",
    sessionCount: filteredLogs.length,
    sessionIds: filteredLogs.map((l) => l.id).filter(Boolean),
    lastSessionDateKey,
    metrics: {
      totalHours: metrics.totalHours,
      totalSessions: metrics.totalSessions,
      homeworkCompletionRate: metrics.homeworkCompletionRate,
      homeworkCompleted: metrics.homeworkCompleted,
      homeworkPartial: metrics.homeworkPartial,
      homeworkNotDone: metrics.homeworkNotDone,
      homeworkNotAssigned: metrics.homeworkNotAssigned,
      avgEffort: metrics.avgEffort,
      avgMotivation: metrics.avgMotivation,
      avgBehavior: metrics.avgBehavior,
      avgFocus: metrics.avgFocus,
      avgConfidence: metrics.avgConfidence,
      riskLevel: metrics.riskLevel,
    },
    normalizedHours: metrics.normalizedHours,
    rawSubjectHours: metrics.rawSubjectHours,
    subjects: metrics.subjects,
    tutors: metrics.tutors,
    topicsCovered: metrics.topicsCovered,
    ai: {
      provider: aiResult.provider || "gemini",
      riskLevel: aiResult.riskLevel || metrics.riskLevel,
      overallProgress: aiResult.overallProgress || "",
      academicProgress: aiResult.academicProgress || "",
      classPerformance: aiResult.classPerformance || "",
      homeworkAnalysis: aiResult.homeworkAnalysis || "",
      learningHabitsNarrative: aiResult.learningHabitsNarrative || "",
      keyStrengths: Array.isArray(aiResult.keyStrengths) ? aiResult.keyStrengths : [],
      areasForImprovement: Array.isArray(aiResult.areasForImprovement) ? aiResult.areasForImprovement : [],
      instructorComments: aiResult.instructorComments || "",
      goalsAndActionPlan: Array.isArray(aiResult.goalsAndActionPlan) ? aiResult.goalsAndActionPlan : [],
    },
  };
}

/**
 * End-to-end report generation: build metrics, request the AI narrative (with
 * a local fallback), persist the report doc, and return it.
 *
 * `filteredLogs` should already be limited to the desired date range; it is
 * sorted internally to derive the last-session date.
 */
export async function createProgressReport({
  studentId,
  studentName,
  filteredLogs,
  startDate,
  endDate,
}) {
  const sorted = sortByDateDesc(filteredLogs);
  const lastSessionDateKey = sorted.length ? toDateKey(sorted[0]?.sessionStart) || "" : "";

  const metrics = buildMetrics(sorted);
  const aiPayload = buildAiPayload(sorted, studentName, startDate, endDate, metrics);

  let aiResult = null;
  try {
    const callable = httpsCallable(firebaseFunctions, "generateSessionLogAi");
    const res = await callable({ mode: "full_progress_report", payload: aiPayload });
    aiResult = res?.data || null;
  } catch (aiErr) {
    console.warn("AI generation failed, using local fallback", aiErr);
  }
  if (!aiResult || !aiResult.overallProgress) {
    aiResult = buildLocalAiFallback(metrics, studentName);
  }

  const now = Date.now();
  const uid = getAuth().currentUser?.uid || "";
  const reportId = buildReportId(studentId, startDate, endDate, now);

  const reportDoc = buildReportDocument({
    reportId,
    studentId,
    studentName,
    startDate,
    endDate,
    generatedAtMs: now,
    generatedByUid: uid,
    filteredLogs: sorted,
    lastSessionDateKey,
    metrics,
    aiResult,
  });

  await saveProgressReport(reportId, reportDoc);
  return reportDoc;
}
